import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { fetchSubscriptions, getSubscriptionByGuid } from '../services/subscriptionService';
import { CAPABILITIES, hasCapability } from '../services/capabilitiesService';
import { match } from 'path-to-regexp';
import CONFIG from '../config/config';

// Import icons from Material-UI
//import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightIcon from '@mui/icons-material/FlightOutlined';
import RouteIcon from '@mui/icons-material/RouteOutlined';
import InventoryIcon from '@mui/icons-material/InventoryOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import WarningIcon from '@mui/icons-material/WarningOutlined';
import ReportIcon from '@mui/icons-material/AssessmentOutlined';
import EngineeringIcon from '@mui/icons-material/EngineeringOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import RollFilmIcon from '@mui/icons-material/CameraRoll';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import { Business as CustomerIcon } from '@mui/icons-material';

import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscriptions, selectedOrg, updateSelectedOrg, refreshSubscriptions } = useOrganization();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  const accountGuid = localStorage.getItem('authSession')
    ? JSON.parse(localStorage.getItem('authSession')).accountGuid
    : null;

  const exclusions = [
    '/missions',
    '/personnel',
    '/billing',
  ];

  const navCategories = [
    {
      category: 'Operations',
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
        { label: 'Missions', path: '/missions', icon: <FlightTakeoffIcon /> },
        ...(subscription && hasCapability(subscription.planId, CAPABILITIES.MAINTENANCE)
          ? [{ label: 'Flight Logs', path: '/flight-logs', icon: <FlightIcon /> }]
          : []),
        ...(subscription && hasCapability(subscription.planId, CAPABILITIES.ASSETS)
          ? [{ label: 'Assets', path: '/assets', icon: <InventoryIcon /> }]
          : []),
        ...(subscription && hasCapability(subscription.planId, CAPABILITIES.MAINTENANCE)
          ? [{ label: 'Maintenance', path: '/maintenance', icon: <BuildIcon /> }]
          : []),
        ...(subscription && hasCapability(subscription.planId, CAPABILITIES.INCIDENTS)
          ? [{ label: 'Incidents', path: '/incidents', icon: <WarningIcon /> }]
          : []),
        //{ label: 'Mapping EZ', path: '/waypoints', icon: <RouteIcon /> },
      ],
    },
    {
      category: 'Management',
      items: [
        { label: 'Personnel', path: '/personnel', icon: <EngineeringIcon /> },
        ...(subscription && hasCapability(subscription.planId, CAPABILITIES.REPORTS)
          ? [{ label: 'Reports', path: '/reports', icon: <ReportIcon /> }]
          : []),
        ...(subscription && hasCapability(subscription.planId, CAPABILITIES.CUSTOMERS)
          ? [{ label: 'Customers', path: '/customers', icon: <CustomerIcon /> }]
          : []),
        //{ separator: true },
      ],
    },
  ];

  useEffect(() => {
    const subscriptionData = subscriptions.find(sub => sub.subscriptionGuid === selectedOrg);
    if (subscriptionData) {
      setSubscription(subscriptionData);
    }
  }, [subscriptions]);

  useEffect(() => {
    const loadSubscriptionsAndData = async () => {
      try {
        await refreshSubscriptions();

        if (subscriptions.length > 0) {
          // Check if selectedOrg exists in the fetched subscriptions
          const existsInData = subscriptions.some(sub => sub.subscriptionGuid === selectedOrg);
          let currentOrg = selectedOrg;

          if (!existsInData && subscriptions.length > 0) {
            // Reset to the first subscription if selectedOrg is invalid
            updateSelectedOrg(subscriptions[0].subscriptionGuid);
          }

          // Find and set subscription data for the selected organization
          const subscriptionData = subscriptions.find(sub => sub.subscriptionGuid === currentOrg);
          if (subscriptionData) {
            setSubscription(subscriptionData);
          } else {
            throw new Error('Subscription not found in loaded data');
          }
        } else {
          // No subscriptions exist
          //updateSelectedOrg(null);
          //localStorage.removeItem('selectedOrg');
          //setSubscription(null);
        }
      } catch (err) {
        setError('Failed to load subscriptions.');
        console.error('Error loading subscriptions and data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionsAndData();
  }, [selectedOrg, updateSelectedOrg]);


  const handleOrgChange = (event) => {
    const orgId = event.target.value;

    // Check if the current path is in the exclusions list
    const isExcluded = exclusions.some((excludedPath) => {
      const routeMatcher = match(excludedPath, { decode: decodeURIComponent });
      return routeMatcher(location.pathname);
    });

    // Redirect only if the current path is NOT in the exclusions list
    if (!isExcluded) {
      navigate('/dashboard');
    }

    updateSelectedOrg(orgId);
  };

  const handleOrgChangeOLD = (event) => {
    if (
      selectedOrg &&
      pagesToRedirect.some((path) => !location.pathname.startsWith(path))
    ) {
      navigate('/dashboard');
    }

    const orgId = event.target.value;
    updateSelectedOrg(orgId);
  };

  const handleAccountClick = (event) => {
    setAccountAnchorEl(accountAnchorEl ? null : event.currentTarget);
  };

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(settingsAnchorEl ? null : event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAccountAnchorEl(null);
  };

  const handleCloseSettingsMenu = () => {
    setSettingsAnchorEl(null);
  };

  const accountMenuOpen = Boolean(accountAnchorEl);
  const settingsMenuOpen = Boolean(settingsAnchorEl);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: CONFIG.MEASURE.SIDEBAR_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: CONFIG.MEASURE.SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#2c3e50',
          color: '#ecf0f1',
          top: '64px',
          paddingTop: '16px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mx: 1,
          my: 1,
        }}
      >
        {loading ? (
          <Typography variant="body2" sx={{ color: '#ecf0f1' }}>
            Loading orgs...
          </Typography>
        ) : error ? (
          <Typography variant="body2" sx={{ color: 'red' }}>
            {error}
          </Typography>
        ) : (
          <FormControl
            sx={{
              width: '100%',
              backgroundColor: '#2c3e50',
              borderRadius: '8px',
              height: '36px',
              '& .MuiInputBase-root': {
                height: '36px',
                fontSize: '0.8rem',
              },
            }}
          >
            <Select
              value={selectedOrg}
              onChange={handleOrgChange}
              sx={{
                color: '#ecf0f1',
                backgroundColor: '#2c3e50',
                borderRadius: '8px',
                height: '100%',
                '& .MuiSelect-icon': { color: '#ecf0f1' },
              }}
            >
              {subscriptions.map((sub) => (
                <MenuItem
                  key={sub.subscriptionGuid}
                  //selected={sub.subscriptionGuid === selectedOrg}
                  onClick={() => updateSelectedOrg(sub.subscriptionGuid)}
                  value={sub.subscriptionGuid}
                  sx={{
                    color: '#2c3e50',
                    '&.Mui-selected': {
                      backgroundColor: '#e0f7fa',
                      color: '#2c3e50',
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  {sub.orgName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <List>
        {navCategories.map((category, catIndex) => (
          <React.Fragment key={`category-${catIndex}`}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                color: '#a0a0a0',
                marginLeft: 2,
                marginTop: catIndex === 0 ? 0 : 2,
                marginBottom: 1,
              }}
            >
              {category.category}
            </Typography>

            {category.items.map((item, index) => {
              if (item.separator) {
                return (
                  <Divider
                    key={`divider-${catIndex}-${index}`}
                    sx={{
                      backgroundColor: '#a0a0a0',
                      marginY: 0.5,
                    }}
                  />
                );
              }

              const isActive = location.pathname === item.path;

              return (
                <ListItem
                  key={`item-${catIndex}-${index}`}
                  component="div"
                  sx={{
                    padding: '6px 24px',
                    backgroundColor: isActive ? '#1a252f' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#1a252f',
                      color: '#ffffff',
                    },
                  }}
                >
                  <Box
                    component={Link}
                    to={item.path}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                      color: isActive ? '#ffffff' : '#ecf0f1',
                      width: '100%',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? '#ffffff' : '#ecf0f1',
                        minWidth: '36px',
                        paddingRight: '0px',
                      }}
                    >
                      {React.cloneElement(item.icon, { sx: { fontSize: '18px' } })}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.85rem',
                          fontWeight: 400,
                          color: isActive ? '#ffffff' : '#ecf0f1',
                        },
                      }}
                    />
                  </Box>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}

        <Divider sx={{ backgroundColor: '#a0a0a0', marginY: 0.5 }} />

        {/* Settings menu */}
        {hasCapability(subscription?.planId, CAPABILITIES.SHOT_LISTS) ? (
          <>
            <ListItem
              button
              onClick={handleSettingsClick}
              sx={{
                padding: '6px 24px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#1a252f',
                  color: '#ffffff',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: '#ecf0f1',
                  minWidth: '36px',
                  paddingRight: '0px',
                }}
              >
                <TuneOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.85rem',
                    fontWeight: 400,
                    color: '#ecf0f1',
                  },
                }}
              />
            </ListItem>

            {hasCapability(subscription?.planId, CAPABILITIES.SHOT_LISTS) ? (
              <>
                <Popper
                  open={settingsMenuOpen}
                  anchorEl={settingsAnchorEl}
                  placement="right-start"
                  sx={{ zIndex: 1300 }}
                >
                  <ClickAwayListener onClickAway={handleCloseSettingsMenu}>
                    <Paper elevation={3} sx={{ padding: 1, backgroundColor: '#2c3e50', color: '#ecf0f1' }}>
                      <List dense>
                        <ListItem
                          button
                          component={Link}
                          to="/settings/predefined-shots"
                          onClick={handleCloseSettingsMenu}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#1a252f',
                            },
                          }}
                        >
                          <ListItemIcon>
                            <RollFilmIcon
                              sx={{
                                color: '#ecf0f1',
                                minWidth: '28px',
                                paddingRight: '0px',
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary="Predefined Shots"
                            sx={{
                              '& .MuiTypography-root': {
                                color: '#ecf0f1', // Match the icon color
                                fontSize: '0.85rem',
                              },
                            }}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </>
            ) : null}
          </>
        ) : null}

        {/* Account menu */}
        {/*
        <ListItem
          button
          onClick={handleAccountClick}
          sx={{
            padding: '6px 24px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#1a252f',
              color: '#ffffff',
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: '#ecf0f1',
              minWidth: '36px',
              paddingRight: '0px',
            }}
          >
            <PersonOutlineIcon />
          </ListItemIcon>
          <ListItemText
            primary="Account"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '0.85rem',
                fontWeight: 400,
                color: '#ecf0f1',
              },
            }}
          />
        </ListItem>
        */}
        {/*
        <Popper
          open={accountMenuOpen}
          anchorEl={accountAnchorEl}
          placement="right-start"
          sx={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleCloseAccountMenu}>
            <Paper elevation={3} sx={{ padding: 1, backgroundColor: '#2c3e50', color: '#ecf0f1' }}>
              <List dense>
                <ListItem
                  button
                  component={Link}
                  to="/account"
                  onClick={handleCloseAccountMenu}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#1a252f',
                    },
                  }}
                >
                  <ListItemIcon>
                    <PersonOutlineIcon sx={{ color: '#ecf0f1' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Settings"
                    sx={{
                      '& .MuiTypography-root': {
                        color: '#ecf0f1', // Match the icon color
                        fontSize: '0.85rem',
                      },
                    }}
                  />
                </ListItem>
                <ListItem
                  button
                  component={Link}
                  to="/billing"
                  onClick={handleCloseAccountMenu}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#1a252f',
                    },
                  }}
                >
                  <ListItemIcon>
                    <CreditCardOutlinedIcon sx={{ color: '#ecf0f1' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Subscriptions"
                    sx={{
                      '& .MuiTypography-root': {
                        color: '#ecf0f1', // Match the icon color
                        fontSize: '0.85rem',
                      },
                    }}
                  />
                </ListItem>
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
        */}
      </List>
    </Drawer>
  );
};

export default Sidebar;